"use client"
import { useMemo } from 'react'
import Markdown from '@/app/common/Markdown'
import { parseAndSplitByH2 } from '@/lib/parseMarkdown'
import { type PerspectiveCategory } from '@/app/common/blocks/perspectiveTabContext'
import { POV_HEADING_IDS } from './plan-a-timeline-diagram/planATimelineDiagramModel'
import { ScenarioEndFooter, type ScenarioEnding } from '../ScenarioEndFooter'

// One of the alternate-POV endings (Public / Insider), expanded IN PLACE under
// the end-of-Plan-A timeline diagram — the same way the epilogue expands. The
// reader has already seen the shared intro on the main path, so this renders
// only the POV's own body (the server strips the intro). It ends on the shared
// scenario end-cap (heading + timeline diagram + reviewer box) so the reader can
// hop to another ending, exactly like the epilogue does.

// The end-cap heading each POV lands on is the matching ScenarioEnding. Kept as
// a literal dict so a cmd-click from the render site reaches it directly.
const POV_ENDING: Record<Exclude<PerspectiveCategory, 'mainScenario'>, ScenarioEnding> = {
  public: 'public',
  insider: 'insider',
}

export const PerspectivePovBody = ({
  perspective,
  markdown,
}: {
  perspective: Exclude<PerspectiveCategory, 'mainScenario'>
  markdown: string
}) => {
  const chunks = useMemo(() => {
    if (!markdown.trim()) return []
    try {
      return parseAndSplitByH2(markdown)
    } catch {
      return []
    }
  }, [markdown])

  return (
    // The id is the scroll/deep-link target the diagram's POV endpoint aims at
    // (see POV_HEADING_IDS + useAnchorDeepLink) — mirrors the epilogue heading id.
    // No top spacing of its own, and the leading h1's margin-top is zeroed (the
    // child selector outranks the global .good-ending-text h1 rule): the 75px
    // gap below the end-cap nav is owned entirely by the endings wrapper in
    // PlaybookBranchNodeMarkdownContent.
    <div id={POV_HEADING_IDS[perspective]}>
      <div className="markdown-content [&>h1:first-child]:mt-0">
        {chunks.map((chunk, i) => (
          <Markdown key={i} nodes={chunk.nodes} docContext={chunk.docContext} />
        ))}
      </div>
      <ScenarioEndFooter ending={POV_ENDING[perspective]} />
    </div>
  )
}
